import React, { useState, useEffect } from 'react';
import { message, Space, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, BookOutlined } from '@ant-design/icons';
import type { ColumnType } from 'antd/lib/table';
import { motion } from 'framer-motion';
import moment from 'moment';
import { tagsApi } from '../../api/tags/tags.api';
import { Tag } from '../../common/tag.interface';
import CModalAddTag from '../../components/modal/CModalAddTag';
import CModalEditTag from '../../components/modal/CModalEditTag';
import CModalViewTagBooks from '../../components/modal/CModalViewTagBooks';
import CTable from '../../components/table/CTable';
import { QUERY_PARAM } from '../../constants/get-api.constant';
import Utils from '../../utils/base-utils';
import './tags.styles.scss';

const TagsPage: React.FC = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isViewBooksModalVisible, setIsViewBooksModalVisible] = useState(false);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [textSearch, setTextSearch] = useState('');
  const [currentSearchValue, setCurrentSearchValue] = useState<any>({
    size: QUERY_PARAM.size,
    offset: 0,
  });

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      const data = await tagsApi.getAllTags();
      setTags(data);
      setTotalRecords(data.length);
    } catch (error) {
      message.error('Không thể tải danh sách tags');
      console.error('Error loading tags:', error);
    }
  };

  const onChangePagination = (page: number) => {
    const newSearchValue = {
      ...currentSearchValue,
      offset: (page - 1) * QUERY_PARAM.size
    };
    setCurrentSearchValue(newSearchValue);
  };

  const onChangeInput = (event: any) => {
    setTextSearch(event.target.value);
  };

  const onSearch = () => {
    const body: any = {
      size: QUERY_PARAM.size,
      offset: 0,
      search: textSearch,
    };
    const finalBody = Utils.getRidOfUnusedProperties(body);
    setCurrentSearchValue(finalBody);
    // Implement search logic here if needed
    loadTags();
  };

  const handleDelete = async (record: Tag) => {
    try {
      await tagsApi.deleteTag(record.id);
      message.success('Xóa tag thành công');
      loadTags();
    } catch (error: any) {
      if (error.response?.status === 409) {
        message.error('Không thể xóa tag đang được sử dụng bởi sách');
      } else {
        message.error('Không thể xóa tag');
      }
      console.error('Error deleting tag:', error);
    }
  };

  const handleEdit = (record: Tag) => {
    setSelectedTag(record);
    setIsEditModalVisible(true);
  };

  const handleViewBooks = (record: Tag) => {
    setSelectedTag(record);
    setIsViewBooksModalVisible(true);
  };

  const handleAddSuccess = () => {
    setIsAddModalVisible(false);
    loadTags();
    message.success('Thêm tag thành công');
  };

  const handleEditSuccess = () => {
    setIsEditModalVisible(false);
    setSelectedTag(null);
    loadTags();
    message.success('Cập nhật tag thành công');
  };

  const columns: ColumnType<Tag>[] = [
    {
      title: 'Số thứ tự',
      width: '120px',
      render: (_, __, rowIndex) => (
        <div className="span-table" style={{ textAlign: 'center' }}>
          {currentSearchValue.offset + rowIndex + 1}
        </div>
      ),
    },
    {
      title: 'Tên Tag',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Tag) => (
        <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <div
            style={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              backgroundColor: record.color || '#ccc',
              border: '1px solid #d9d9d9'
            }}
          />
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => (
        <div style={{ textAlign: 'center' }}>
          {text || '-'}
        </div>
      ),
    },
    {
      title: 'Màu sắc',
      dataIndex: 'color',
      key: 'color',
      render: (color: string) => (
        <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '4px',
              backgroundColor: color || '#ccc',
              border: '1px solid #d9d9d9'
            }}
          />
          <span style={{ fontSize: '12px' }}>{color || '-'}</span>
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <div style={{ textAlign: 'center' }}>
          <span 
            style={{ 
              color: isActive ? '#52c41a' : '#ff4d4f',
              fontWeight: 500
            }}
          >
            {isActive ? 'Hoạt động' : 'Vô hiệu hóa'}
          </span>
        </div>
      ),
    },
    {
      title: 'Thời gian tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => (
        <div style={{ textAlign: 'center' }}>
          {moment(date).format('DD/MM/YYYY HH:mm:ss')}
        </div>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      dataIndex: 'action',
      render: (_, record: Tag) => (
        <Space size="middle" style={{ display: 'flex', justifyContent: 'center' }}>
          <Tooltip placement="top" title="Xem sách">
            <BookOutlined 
              style={{ color: '#1890ff', cursor: 'pointer' }} 
              onClick={() => handleViewBooks(record)} 
            />
          </Tooltip>
          <Tooltip placement="top" title="Chỉnh sửa">
            <EditOutlined onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip placement="top" title="Xóa">
            <DeleteOutlined 
              style={{ color: '#ff4d4f', cursor: 'pointer' }} 
              onClick={() => handleDelete(record)} 
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <motion.div
      className="sketch-main"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <CModalAddTag
        visible={isAddModalVisible}
        onCancel={() => setIsAddModalVisible(false)}
        onSuccess={handleAddSuccess}
      />

      <CModalEditTag
        visible={isEditModalVisible}
        tag={selectedTag}
        onCancel={() => {
          setIsEditModalVisible(false);
          setSelectedTag(null);
        }}
        onSuccess={handleEditSuccess}
      />

      <CModalViewTagBooks
        visible={isViewBooksModalVisible}
        tag={selectedTag}
        onCancel={() => {
          setIsViewBooksModalVisible(false);
          setSelectedTag(null);
        }}
      />

      <div className="table-area">
        <CTable
          tableMainTitle="Danh sách Tags"
          allowTextSearch={true}
          onChangeInput={onChangeInput}
          onSearch={onSearch}
          titleOfColumnList={columns}
          data={tags}
          totalRecord={totalRecords}
          titleAddButton="Thêm Tag"
          textSearch="Nhập tên tag"
          onChangePagination={onChangePagination}
          allowAddObject={true}
          pageSize={10}
          size={450}
          handleAddObject={() => setIsAddModalVisible(true)}
        />
      </div>
    </motion.div>
  );
};

export default TagsPage;
